/**
 * The contacts directory's pure half — search, filters, ordering, initials.
 *
 * SHAPED BY WHAT THE ROWS ACTUALLY HOLD, measured across CDT, DGS and CDTFA
 * (69 contacts) on 2026-09-17:
 *
 *   title      49/69     email      30/69     linkedin_url  35/69
 *   phone       4/69     org_unit    6/69
 *   influence_level  'unknown' for 56; decision_maker 5, influencer 5
 *   relationship_status  'new' for 65, everything else 4
 *
 * So there is no phone column and no org-unit column — a column blank on
 * nineteen rows in twenty is dead space that makes the real fields harder to
 * scan. Both still render inline on the rows that have them.
 *
 * And there is no relationship filter: a chip that selects 65 of 69 rows is a
 * control that does nothing. The one filter worth its place is KEY PEOPLE —
 * decision makers and influencers, 10 of 69, which is exactly the question a
 * rep opens this list to ask.
 *
 * No Gravatar either, and that was measured rather than assumed: all 30 email
 * addresses across those three accounts resolve to `*.ca.gov`, and 0 of 30
 * have a Gravatar. It would have shown a photograph of nobody while sending an
 * email hash per contact to a third party on every render.
 */

/** Everything a search term is matched against, lowercased once per contact. */
export function haystack(contact) {
  if (!contact) return '';
  return [
    contact.first_name,
    contact.last_name,
    contact.title,
    contact.email,
    contact.org_unit,
  ].filter(Boolean).join(' ').toLowerCase();
}

/**
 * Every term must appear somewhere. AND, not OR: typing more words narrows,
 * which is what a directory search has to do — "roy tech" should find one
 * person, not everyone called Roy plus everyone in technology.
 *
 * Diacritics are folded, so "jose" finds "José". The rows are Californian
 * state staff and accented names are common enough that not folding them
 * makes those people unfindable by anyone typing on a US keyboard.
 */
export function matchesQuery(contact, query) {
  const terms = fold(String(query || '')).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = fold(haystack(contact));
  return terms.every((t) => hay.includes(t));
}

/** Lowercase and strip combining marks. */
function fold(s) {
  return String(s == null ? '' : s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/** Decision makers and influencers — the people a rep came here to find. */
export function isKeyPerson(contact) {
  const level = contact && contact.influence_level;
  return level === 'decision_maker' || level === 'influencer';
}

export const FILTERS = {
  key: { label: 'Key people', test: isKeyPerson },
  email: { label: 'Has email', test: (c) => !!(c && c.email) },
  linkedin: { label: 'On LinkedIn', test: (c) => !!(c && c.linkedin_url) },
};

/**
 * Apply the search box and whichever chips are on.
 *
 * Filters AND together with each other and with the query, so the count under
 * the box always describes the rows on screen.
 */
export function applyContactFilter(contacts, { query = '', filters = [] } = {}) {
  const active = filters.filter((f) => FILTERS[f]);
  return (Array.isArray(contacts) ? contacts : [])
    .filter((c) => matchesQuery(c, query))
    .filter((c) => active.every((f) => FILTERS[f].test(c)));
}

/**
 * How many rows a chip would leave, so a chip that selects nothing can say so.
 *
 * Counted against what the OTHER active constraints leave, not against the
 * whole account — which is the only reading that makes the sentence above
 * true. Counted over everything, the chips described an account the reader was
 * no longer looking at: search "brockman" on Franchise Tax Board and the list
 * is one person with no email, while the chips still read "Has email 12" and
 * stay enabled. Pressing one promised twelve rows and delivered "Nothing
 * matches".
 *
 * So for a chip that is OFF this answers "how many you would have if you
 * turned it on", and for one that is ON, "how many it is leaving". Both are
 * the number under your finger.
 *
 * NOTE the asymmetry this creates for the caller: an ON chip can now legally
 * count 0 (turn on Key people, then search for someone who is not one), and
 * disabling it there would strand the reader with a filter they cannot switch
 * off. Only an OFF chip may be disabled.
 */
export function filterCounts(contacts, { query = '', filters = [] } = {}) {
  const list = Array.isArray(contacts) ? contacts : [];
  const active = filters.filter((f) => FILTERS[f]);
  const out = {};
  for (const key of Object.keys(FILTERS)) {
    const combined = active.includes(key) ? active : [...active, key];
    out[key] = applyContactFilter(list, { query, filters: combined }).length;
  }
  return out;
}

export function displayName(contact) {
  if (!contact) return '';
  const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  // A contact with no name at all is real — harvested rows can arrive with an
  // address and nothing else — and "" would render an unclickable blank row.
  return name || contact.email || 'Unnamed contact';
}

/**
 * Up to two letters. Falls back to the email's first character so a nameless
 * row still gets a disc rather than an empty circle.
 *
 * DELIBERATELY NOT VIA `displayName`. Routing the fallback through it returned
 * "U" for a contact with nothing on it — the first letter of the words
 * "Unnamed contact" — which draws a disc that reads as a real person whose
 * name begins with U. A row we know nothing about has to look like one.
 */
export function initials(contact) {
  const first = (contact && contact.first_name ? String(contact.first_name) : '').trim();
  const last = (contact && contact.last_name ? String(contact.last_name) : '').trim();
  const letters = `${first.charAt(0)}${last.charAt(0)}`.trim();
  if (letters) return letters.toUpperCase();
  const email = (contact && contact.email ? String(contact.email) : '').trim();
  const ch = email.replace(/[^\p{L}\p{N}]/gu, '').charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

/**
 * A stable tone per person, so the same face keeps the same colour between
 * renders and between sessions. Hashed from the name rather than the array
 * index — an index would recolour everyone the moment the list is filtered.
 */
export const AVATAR_TONES = 6;
export function avatarTone(contact) {
  const seed = displayName(contact);
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % AVATAR_TONES;
}

/**
 * Key people first, then by surname.
 *
 * Surname, not the rendered name: a directory sorted on "Ayushi Roy" files her
 * under A, and nobody looking for Roy scans the As.
 */
export function sortContacts(contacts) {
  const key = (c) => (isKeyPerson(c) ? 0 : 1);
  const surname = (c) => fold((c && c.last_name) || displayName(c));
  return [...(Array.isArray(contacts) ? contacts : [])].sort(
    (a, b) => key(a) - key(b)
      || surname(a).localeCompare(surname(b))
      || fold(displayName(a)).localeCompare(fold(displayName(b))),
  );
}
